import React, { useMemo, useState, useEffect, useCallback } from "react";
import { AlertTriangle, Bell, CheckCircle2, Info, MessageSquare, X } from "lucide-react";
import { toast } from "sonner";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { subscribeUserDevices } from "../services/deviceService";
import { subscribeDeviceAlerts } from "../services/notificationService";
import { userApiService } from "../services/userApiService";
import AppUpdateNotificationCard from "../components/AppUpdateNotificationCard";

const NotificationPage = () => {
const [userDevices, setUserDevices] = useState({});
const [deviceNotifications, setDeviceNotifications] = useState({});
const [firmwareOffers, setFirmwareOffers] = useState([]);
const [irrigationRequests, setIrrigationRequests] = useState([]);
const [selectedDevice, setSelectedDevice] = useState("");
const [severityFilter, setSeverityFilter] = useState("all");
const [appMessages, setAppMessages] = useState([]);

const { user } = useAuth();

const refreshAppMessages = useCallback(async () => {
  try {
    const data = await userApiService.listNotifications({ limit: 50 });
    const items = (data?.items || []).filter((item) =>
      String(item?.metric || "").startsWith("admin_")
    );
    setAppMessages(items);
  } catch {
    // non-blocking
  }
}, []);

const refreshIrrigationRequests = useCallback(async () => {
  try {
    const data = await userApiService.listPendingIrrigationConsent();
    setIrrigationRequests(data?.items || []);
  } catch {
    // non-blocking
  }
}, []);

useEffect(() => {
  if (!user) return;

  const unsub = subscribeUserDevices(user.uid, (devices) => {
    setUserDevices(devices);

    const deviceIds = Object.keys(devices);
    if (deviceIds.length > 0 && !selectedDevice) {
      setSelectedDevice(deviceIds[0]);
    }
  });

  return () => unsub();
}, [selectedDevice, user]);

useEffect(() => {
  if (Object.keys(userDevices).length === 0) return;

  const listeners = [];

  // Check alerts for each device
  Object.keys(userDevices).forEach((deviceId) => {
    const unsubAlerts = subscribeDeviceAlerts(deviceId, (alertData) => {
      setDeviceNotifications((prev) => ({
        ...prev,
        [deviceId]: alertData,
      }));
    });
    listeners.push(unsubAlerts);
  });

  return () => {
    listeners.forEach((fn) => fn());
  };
}, [userDevices]);

useEffect(() => {
  if (!user) return;
  userApiService.listFirmwareOffers().then((data) => setFirmwareOffers(data?.items || [])).catch(() => {});
  refreshIrrigationRequests();
  refreshAppMessages();
  const timer = setInterval(() => {
    userApiService.listFirmwareOffers().then((data) => setFirmwareOffers(data?.items || [])).catch(() => {});
    refreshIrrigationRequests();
    refreshAppMessages();
  }, 60000);
  return () => clearInterval(timer);
}, [user, refreshIrrigationRequests, refreshAppMessages]);

// Format timestamp into Date and Time
const getFormattedDate = (timestamp) => {
  if (!timestamp) return "Invalid Date";
  const [date] = timestamp.split("_");
  return date;
};

const getFormattedTime = (timestamp) => {
  if (!timestamp) return "Invalid Time";
  const [, time] = timestamp.split("_");
  return time;
};

const selectedAlerts = Object.entries(deviceNotifications[selectedDevice] || {})
  .map(([timestamp, data]) => ({ timestamp, ...(data || {}) }))
  .sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1))
  .filter((item) =>
    severityFilter === "all"
      ? true
      : (item.type || "info").toLowerCase() === severityFilter
  );

const stats = useMemo(() => {
  const list = Object.entries(deviceNotifications[selectedDevice] || {})
    .map(([timestamp, data]) => ({ timestamp, ...(data || {}) }))
    .sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
  const byType = { critical: 0, warning: 0, info: 0 };
  list.forEach((a) => {
    const t = (a.type || "info").toLowerCase();
    if (t === "critical" || t === "warning" || t === "info") byType[t] += 1;
  });
  return { total: list.length, ...byType };
}, [deviceNotifications, selectedDevice]);

return (
  <PageShell>
    <PageHero
      eyebrow="App"
      title="Notifications."
      highlight="Stay ahead."
      description="See device alerts in one place. Filter by severity and act fast when conditions drift."
    />

    <PageSection variant="content" className="pb-24">
      <div className="mx-auto max-w-6xl grid gap-6 min-w-0 w-full box-border">
        <AppUpdateNotificationCard />
        {appMessages.length ? (
          <Card className="shadow-soft border-accent/30 min-w-0 overflow-hidden">
            <CardHeader className="p-4 sm:p-6 min-w-0">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Messages from Smart Farming
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 p-4 sm:p-6 pt-0 min-w-0">
              {appMessages.map((item) => (
                <div key={item.id} className="rounded-2xl border border-border p-4">
                  <div className="flex items-start gap-3">
                    <Info className="h-4 w-4 mt-1 text-accent shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-sm">
                        {item.title ||
                          (item.metric === "admin_offer"
                            ? "Special offer"
                            : item.metric === "admin_announcement"
                              ? "Announcement"
                              : "Update")}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{item.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ) : null}
        {irrigationRequests.length ? (
          <Card className="shadow-soft border-warning/30 min-w-0 overflow-hidden">
            <CardHeader className="p-4 sm:p-6 min-w-0">
              <CardTitle className="text-base sm:text-lg">Low soil — start pump?</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 p-4 sm:p-6 pt-0 min-w-0">
              {irrigationRequests.map((req) => (
                <div key={req.requestId} className="rounded-2xl border border-border p-4 grid gap-3">
                  <div>
                    <div className="font-medium">{req.deviceName || req.deviceId}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Soil {req.soilValue ?? "—"}% is below {req.threshold ?? "—"}%.
                      {req.notifyCount ? ` Reminder ${req.notifyCount}.` : ""}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Approve to start the pump now. Deny to block auto-start until soil recovers.
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full">
                    <Button
                      className="w-full sm:w-auto"
                      onClick={async () => {
                        try {
                          await userApiService.approveIrrigationConsent(req.requestId);
                          toast.success("Pump start requested");
                          await refreshIrrigationRequests();
                        } catch (e) {
                          toast.error(e?.message || "Could not start pump");
                        }
                      }}
                    >
                      Start pump
                    </Button>
                    <Button
                      className="w-full sm:w-auto"
                      variant="outline"
                      onClick={async () => {
                        try {
                          await userApiService.denyIrrigationConsent(req.requestId);
                          toast.success("Auto irrigation declined for this dry spell");
                          await refreshIrrigationRequests();
                        } catch (e) {
                          toast.error(e?.message || "Decline failed");
                        }
                      }}
                    >
                      Do not start
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ) : null}

        {firmwareOffers.length ? (
          <Card className="shadow-soft border-accent/30 min-w-0 overflow-hidden">
            <CardHeader className="p-4 sm:p-6 min-w-0">
              <CardTitle className="text-base sm:text-lg">Firmware updates available</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 p-4 sm:p-6 pt-0 min-w-0">
              {firmwareOffers.map((offer) => (
                <div key={offer.releaseId} className="rounded-2xl border border-border p-4 grid gap-3">
                  <div>
                    <div className="font-medium">Version {offer.version}</div>
                    <div className="text-sm text-muted-foreground mt-1">{offer.notes || "Device firmware update"}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Devices: {(offer.devices || []).map((d) => d.deviceId).join(", ")}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full">
                    <Button
                      className="w-full sm:w-auto"
                      onClick={async () => {
                        try {
                          await userApiService.acceptFirmwareOffer(offer.releaseId);
                          toast.success(`Accepted v${offer.version}. Devices will update when online.`);
                          const data = await userApiService.listFirmwareOffers();
                          setFirmwareOffers(data?.items || []);
                        } catch (e) {
                          toast.error(e?.message || "Accept failed");
                        }
                      }}
                    >
                      Accept update
                    </Button>
                    <Button
                      className="w-full sm:w-auto"
                      variant="outline"
                      onClick={async () => {
                        try {
                          await userApiService.rejectFirmwareOffer(offer.releaseId);
                          toast.success("Update declined");
                          const data = await userApiService.listFirmwareOffers();
                          setFirmwareOffers(data?.items || []);
                        } catch (e) {
                          toast.error(e?.message || "Reject failed");
                        }
                      }}
                    >
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ) : null}

        <Card className="shadow-soft min-w-0 overflow-hidden w-full">
          <CardHeader className="min-w-0 p-4 sm:p-6">
            <CardTitle className="flex flex-col gap-4 min-w-0">
              <span className="flex items-center gap-2 shrink-0">
                <Bell className="h-5 w-5 text-accent" />
                Alerts feed
              </span>
              <div className="flex flex-col w-full min-w-0 gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <select
                  value={selectedDevice}
                  onChange={(e) => setSelectedDevice(e.target.value)}
                  className="w-full sm:flex-1 sm:min-w-[10rem] rounded-2xl border border-input bg-cream/50 px-4 py-3 text-sm outline-none"
                >
                  {!Object.keys(userDevices).length && <option value="">No devices</option>}
                  {Object.keys(userDevices).map((id) => (
                    <option key={id} value={id}>
                      {userDevices[id]?.name || `Device ${id}`}
                    </option>
                  ))}
                </select>
                <select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  className="w-full sm:flex-1 sm:min-w-[10rem] rounded-2xl border border-input bg-cream/50 px-4 py-3 text-sm outline-none"
                >
                  <option value="all">All severity</option>
                  <option value="critical">Critical</option>
                  <option value="warning">Warning</option>
                  <option value="info">Info</option>
                </select>
                <Button
                  variant="outline"
                  className="w-full sm:w-auto shrink-0"
                  onClick={() => toast("Tip", { description: "Use Alerts page to configure threshold rules." })}
                >
                  Tips
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 min-w-0 p-4 sm:p-6 pt-0">
            <div className="rounded-2xl border border-border bg-cream/50 p-3 sm:p-4 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</div>
              <div className="mt-2 font-display text-2xl sm:text-3xl">{stats.total}</div>
            </div>
            <div className="rounded-2xl border border-border bg-cream/50 p-3 sm:p-4 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Critical</div>
              <div className="mt-2 font-display text-2xl sm:text-3xl text-destructive">{stats.critical}</div>
            </div>
            <div className="rounded-2xl border border-border bg-cream/50 p-3 sm:p-4 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Warning</div>
              <div className="mt-2 font-display text-2xl sm:text-3xl text-warning">{stats.warning}</div>
            </div>
            <div className="rounded-2xl border border-border bg-cream/50 p-3 sm:p-4 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Info</div>
              <div className="mt-2 font-display text-2xl sm:text-3xl text-info">{stats.info}</div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6 min-w-0">
          {selectedAlerts.map((a) => {
            const type = (a.type || "info").toLowerCase();
            const icon =
              type === "critical" ? AlertTriangle : type === "warning" ? MessageSquare : type === "info" ? Info : CheckCircle2;
            const Icon = icon;
            const tone =
              type === "critical"
                ? "border-destructive/30 bg-destructive/5"
                : type === "warning"
                  ? "border-warning/30 bg-warning/10"
                  : "border-border bg-cream/30";
            return (
              <div key={a.timestamp} className={`rounded-3xl border p-4 sm:p-6 shadow-soft min-w-0 overflow-hidden ${tone}`}>
                <div className="flex items-start justify-between gap-3 min-w-0">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <span className="h-10 w-10 shrink-0 rounded-2xl bg-card border border-border flex items-center justify-center">
                      <Icon className="h-5 w-5 text-accent" />
                    </span>
                    <div className="min-w-0">
                      <div className="font-medium text-foreground">
                        {userDevices[selectedDevice]?.name || `Device ${selectedDevice || "—"}`}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {getFormattedDate(a.timestamp)} {getFormattedTime(a.timestamp)}
                      </div>
                    </div>
                  </div>
                  <span className="shrink-0 text-[10px] uppercase tracking-widest rounded-full border border-border bg-card px-3 py-1 text-muted-foreground">
                    {type}
                  </span>
                </div>

                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                  {a.message || "No message"}
                </p>

                <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-2 w-full">
                  <Button className="w-full sm:w-auto" variant="outline" onClick={() => (window.location.href = "/history")}>
                    View history
                  </Button>
                  <Button className="w-full sm:w-auto" variant="outline" onClick={() => (window.location.href = "/settings")}>
                    Settings
                  </Button>
                  <Button className="w-full sm:w-auto" variant="ghost" onClick={() => toast("Dismissed", { description: "This is UI-only for now. We can persist dismiss state in Firebase next." })}>
                    <X className="h-4 w-4" /> Dismiss
                  </Button>
                </div>
              </div>
            );
          })}

          {!selectedAlerts.length && (
            <div className="md:col-span-2 rounded-3xl border border-border bg-card p-6 sm:p-10 shadow-soft text-center min-w-0 w-full box-border">
              <div className="font-display text-xl sm:text-2xl">No alerts found</div>
              <p className="mt-2 text-sm text-muted-foreground">
                No alerts match the selected filter for this device.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row flex-wrap justify-center gap-3 w-full max-w-full mx-auto">
                <Button className="w-full sm:w-auto" variant="outline" onClick={() => (window.location.href = "/alerts")}>
                  Configure rules
                </Button>
                <Button className="w-full sm:w-auto" onClick={() => (window.location.href = "/devices")}>
                  Devices center
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </PageSection>
  </PageShell>
);
};

export default NotificationPage;
